import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import yup from "../../formsvalidation/yup";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import ButtonLoader from "../../atoms/buttonLoader";
import {
  CreateWorker,
  getWorkerDetails,
  UpdateWorker,
  CreateManager,
  UpdateManager,
} from "../../services/service";
import { CustomToast } from "../../atoms/toastMessage";
import { useLocation, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import Phone from "../../atoms/phone";
import { isValidPhoneNumber } from "libphonenumber-js";

const normalizeGenderFromApi = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

  if (!normalized) return "";
  if (normalized === "other" || normalized === "prefer_not_say" || normalized === "prefer_not_to_say") {
    return "prefer_not_to_say";
  }
  if (normalized === "non_binary") return "non-binary";
  if (normalized === "two_spirit") return "two-spirit";
  if (normalized === "male" || normalized === "female") return normalized;
  return value;
};

const normalizeGenderForApi = (value) => {
  if (value === "prefer_not_to_say") return "other";
  return value;
};

const AddWorker = () => {
  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "non-binary", label: "Non-binary" },
    { value: "two-spirit", label: "Two-Spirit" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [workerLoader, setWorkerLoader] = useState(false);
  const [workerData, setWorkerData] = useState([]);
  const [imageRemoved, setImageRemoved] = useState(false);
  const fileInputRef = React.useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { isManager, managerId, managerData } = location.state || {};
  const workerId = location.state?.workerId;
  const { commonValidations } = yup;

  useEffect(() => {
    if (workerId || (isManager && managerId)) {
      setWorkerLoader(true);
      const fetchData = async () => {
        try {
          if (isManager && managerData) {
            setWorkerData(managerData);
          } else {
            const response = await getWorkerDetails(workerId);
            setWorkerData(response?.data?.worker || []);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setWorkerLoader(false);
        }
      };
      fetchData();
    } else {
      // Clear worker data and reset form when in "add" mode
      setWorkerData(null);
      formik.resetForm();
    }
  }, [workerId, managerId, isManager, managerData]);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      phone: "",
      gender: "",
      experience: "",
      projects: "",
      expertise: "",
      profile: null,
      bio: "",
    },
    validationSchema: Yup.object({
      name: commonValidations.name(),
      email: commonValidations.email(),
      ...(workerId ? {} : { password: commonValidations.password() }),
      phone: commonValidations.phone(),
      gender: commonValidations.gender(),
      experience: Yup.number()
        .typeError("Experience must be a number")
        .required("Experience is required"),
      expertise: Yup.string().trim().required("Expertise is required"),
    }),

    onSubmit: (values, { setSubmitting }) => {
      console.log("Form submitted with values:", values);
      const formData = new FormData();

      formData.append("username", values?.name);
      formData.append("email", values?.email);
      formData.append("password", values?.password);
      formData.append("phoneNo", values?.phone);
      formData.append("gender", normalizeGenderForApi(values?.gender));
      formData.append("experience", values?.experience);
      formData.append("expertise", values?.expertise);
      formData.append("bio", values?.bio || "");
      
      // Handle image - send new image or indicate removal
      if (values?.profile) {
        formData.append("image", values.profile);
      } else if (imageRemoved) {
        formData.append("removeImage", "true");
      }

      setIsLoading(true);

      let action;
      if (isManager) {
        action = managerId 
          ? UpdateManager(managerId, formData)
          : CreateManager(formData);
      } else {
        action = workerId
          ? UpdateWorker(workerId, formData)
          : CreateWorker(formData);
      }

      action
        .then((response) => {
          CustomToast({
            type: "success",
            message: `${isManager ? 'Manager' : 'Worker'} ${managerId || workerId ? "updated" : "added"} successfully`,
          });
          setIsLoading(false);
          setSubmitting(false);
          navigate(isManager ? "/organization" : "/view-worker");
        })
        .catch((error) => {
          CustomToast({
            type: "error",
            message: `${error?.response?.data?.message}`,
          });
          setIsLoading(false);
          setSubmitting(false);
        });
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    formik.setFieldValue("profile", file);
    setImageRemoved(false);
  };

  const handleRemoveFile = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    formik.setFieldValue("profile", null);
    setImageRemoved(true);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (workerData) {
      formik.setValues({
        name: workerData.username || "",
        email: workerData.email || "",
        password: "",
        phone: workerData.phoneNo || "",
        gender: normalizeGenderFromApi(workerData.gender),
        experience: workerData.experience || "",
        projects: workerData.numOfProjects || "",
        expertise: workerData.expertise || "",
        bio: workerData.bio || "",
        profile: workerData?.image || "",
      });
      setImageRemoved(false);
    } else if (!workerId && !managerId) {
      // Reset form to initial values when in "add" mode
      formik.setValues({
        name: "",
        email: "",
        password: "",
        phone: "",
        gender: "",
        experience: "",
        projects: "",
        expertise: "",
        bio: "",
        profile: null,
      });
      setImageRemoved(false);
    }
  }, [workerData, workerId, managerId]);

  console.log("workin", workerData);

  return (
    <div className="w-full flex flex-col gap-6 pt-8 pb-40">
      <ListHeader
        mainHeading={isManager ? "Manager" : "Worker"}
        placeholder="Search"
        btnShow={true}
        linkBreadCrum={isManager ? "/organization" : "/view-worker"}
        blinkBreadCrumText={isManager ? "Organization" : "Worker"}
        blinkBreadCrumText3={`${managerId || workerId ? "Update" : "Add"} ${isManager ? "Manager" : "Worker"}`}
        searchShow={false}
        showLikeFilter={false}
        DropDown={false}
      />
      {
        workerLoader ? (
            <Spin/>
        ):
        <>
        <div className="p-6 w-full bg-white border border-[#DDDDDD] rounded-[8px]">
           <h2 className="text-[20px] font-semibold text-[#1E3A8A]  mb-4">
             {managerId || workerId ? "Update" : "Add"} {isManager ? "Manager" : "Worker"}
           </h2>
           <form onSubmit={formik.handleSubmit} className="space-y-4">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                 <input
                   type="text"
                   placeholder="Name"
                   name="name"
                   className={`mt-1 block w-full h-[52px] px-2 border focus:outline-none border-[#E9E9E9]
                    rounded-[4px]`}
                   onChange={formik.handleChange}
                   onBlur={formik.handleBlur}
                   value={formik.values.name}
                 />
                 {formik.touched.name && formik.errors.name && (
                   <p className="text-red-500 text-sm">{formik.errors.name}</p>
                 )}
               </div>
              <div className="">
                {formik.values.profile ? (
                  <div className="flex items-center w-full h-[52px] mt-1 p-2 border rounded gap-4 overflow-hidden">
                    <p className="text-gray-700 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {formik.values.profile?.name
                        ? formik.values.profile?.name
                        : (workerData?.image || "")}
                    </p>
                    <button
                      onClick={handleRemoveFile}
                      type="button"
                      className="shrink-0 text-red-500 hover:text-red-700"
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="profile"
                    onChange={handleFileChange}
                    className={`w-full max-w-full h-[52px] px-2 pt-[11px] mt-1 border rounded gap-4 focus:outline-none ${
                      formik.touched.profile && formik.errors.profile
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                )}
              </div>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                 <input
                   type="email"
                   name="email"
                   placeholder="Email"
                   className={`mt-1 block w-full h-[52px] px-2 border focus:outline-none border-[#E9E9E9]
                       rounded-[4px]`}
                   onChange={formik.handleChange}
                   onBlur={formik.handleBlur}
                   value={formik.values.email}
                 />
                 {formik.touched.email && formik.errors.email && (
                   <p className="text-red-500 text-sm">{formik.errors.email}</p>
                 )}
               </div>
   
               {!workerId && (
                 <div>
                   <input
                     type="password"
                     name="password"
                     placeholder="Password"
                     className={`mt-1 block w-full h-[52px] px-2 border focus:outline-none border-[#E9E9E9]
                         rounded-[4px]`}
                     onChange={formik.handleChange}
                     onBlur={formik.handleBlur}
                     value={formik.values.password}
                   />
                   {formik.touched.password && formik.errors.password && (
                     <p className="text-red-500 text-sm">{formik.errors.password}</p>
                   )}
                 </div>
               )}
   
               <div>
                 <Phone
                   name="phone"
                   value={formik.values.phone}
                   handleChange={(value, name) => formik.setFieldValue(name, value)}
                   field={{
                     onChange: formik.handleChange,
                     onBlur: formik.handleBlur,
                     ref: formik.getFieldProps("phone").ref,
                   }}
                   label="Phone Number"
                 />
               </div>
   
               <div>
                 <select
                   name="gender"
                   className={`mt-1 block w-full h-[52px] px-2 focus:outline-none border border-[#E9E9E9]
                       rounded-[4px]`}
                   placeholder="Gender"
                   onChange={formik.handleChange}
                   onBlur={formik.handleBlur}
                   value={formik.values.gender}
                 >
                   <option value="">Select Gender</option>
                   {genderOptions.map((option) => (
                     <option key={option.value} value={option.value}>
                       {option.label}
                     </option>
                   ))}
                 </select>
                 {formik.touched.gender && formik.errors.gender && (
                   <p className="text-red-500 text-sm">{formik.errors.gender}</p>
                 )}
               </div>
   
               <div>
                 <input
                   type="number"
                   name="experience"
                   placeholder="Years of experience in this field"
                   className={`mt-1 block w-full h-[52px] px-2 focus:outline-none border border-[#E9E9E9]
                       rounded-[4px]`}
                   onChange={formik.handleChange}
                   onBlur={formik.handleBlur}
                   value={formik.values.experience}
                 />
                 {formik.touched.experience && formik.errors.experience && (
                   <p className="text-red-500 text-sm">
                     {formik.errors.experience}
                   </p>
                 )}
               </div>
               <div>
                 <input
                   type="text"
                   name="expertise"
                   placeholder="Expertise"
                   className={`mt-1 block w-full h-[52px] px-2 border focus:outline-none border-[#E9E9E9]
                   rounded-[4px]`}
                   onChange={formik.handleChange}
                   onBlur={formik.handleBlur}
                   value={formik.values.expertise}
                 />
                 {formik.touched.expertise && formik.errors.expertise && (
                   <p className="text-red-500 text-sm">
                     {formik.errors.expertise}
                   </p>
                 )}
               </div>
             </div>
   
             <div>
               <textarea
                 name="bio"
                 placeholder="Bio (Optional)"
                 className={`mt-1 block w-full h-[156px] p-2 border focus:outline-none border-[#E9E9E9]
                   rounded-[4px]`}
                 onChange={formik.handleChange}
                 value={formik.values.bio}
               />
             </div>
             
             <div className="mt-6">
               <button
                 type="submit"
                 className="w-[152px] h-[44px] bg-[#F97316] text-white font-medium text-[20px] rounded-[4px] hover:bg-orange-600"
               >
                 {isLoading ? (
                   <ButtonLoader />
                 ) : (
                   <p>{(managerId || workerId) ? "Update" : `Add ${isManager ? "Manager" : "Worker"}`}</p>
                 )}
               </button>
             </div>
           </form>
         </div>
        </>

      }
    
    </div>
  );
};

export default AddWorker;
